import {
  Anchor,
  Box,
  Button,
  ColorInput,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { ColorPaletteConfig, ExportBrandingConfigData } from '../types';
import SectionHeader from './SectionHeader';

type Props = {
  config: ExportBrandingConfigData;
  onChange: (config: ExportBrandingConfigData) => void;
  onResetPalette?: () => Promise<void>;
  isResetting?: boolean;
};

type ExtendedPaletteKey =
  | 'primary_light'
  | 'accent_warm'
  | 'accent_cool'
  | 'accent_bright'
  | 'bg'
  | 'row_alt'
  | 'text'
  | 'text_mute'
  | 'border';

type ExtendedFieldDef = {
  key: ExtendedPaletteKey;
  label: string;
  description: string;
  // Shown as the input placeholder when the field is left blank, describing what
  // materialised() derives. Must match the backend derivation in ExportBrandingConfig.
  derivedHint: string;
};

const EXTENDED_FIELDS: ExtendedFieldDef[] = [
  {
    key: 'primary_light',
    label: 'Primary light',
    description: 'Light variant',
    derivedHint: 'Auto: lightened primary',
  },
  {
    key: 'accent_warm',
    label: 'Warm accent',
    description: 'Soft fills',
    derivedHint: 'Auto: from secondary',
  },
  {
    key: 'accent_cool',
    label: 'Cool accent',
    description: 'Dark panels',
    derivedHint: 'Auto: dark shade of primary',
  },
  {
    key: 'accent_bright',
    label: 'Bright accent',
    description: 'Highlights',
    derivedHint: 'Auto: from accent',
  },
  {
    key: 'bg',
    label: 'Background',
    description: 'Page fill',
    derivedHint: 'Auto: tint of primary',
  },
  {
    key: 'row_alt',
    label: 'Alt row',
    description: 'Alt table row',
    derivedHint: 'Auto: tint of primary',
  },
  {
    key: 'text',
    label: 'Body text',
    description: 'Body copy',
    derivedHint: 'Auto: dark shade of primary',
  },
  {
    key: 'text_mute',
    label: 'Muted text',
    description: 'Captions',
    derivedHint: 'Auto: muted shade of primary',
  },
  { key: 'border', label: 'Border', description: 'Dividers', derivedHint: 'Auto: tint of primary' },
];

export default function ColorPaletteSection({
  config,
  onChange,
  onResetPalette,
  isResetting,
}: Props) {
  const palette = config.color_palette;
  const [resetOpened, { open: openReset, close: closeReset }] = useDisclosure(false);

  const handleChange = (updated: ColorPaletteConfig) => {
    onChange({ ...config, color_palette: updated });
  };

  const handleFieldChange = (key: ExtendedPaletteKey, value: string) => {
    handleChange({ ...palette, [key]: value });
  };

  const handleResetConfirmed = async () => {
    if (onResetPalette) {
      await onResetPalette();
    }
    closeReset();
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <SectionHeader
          title="Colour palette"
          subtitle="Tokens used across every generated document, deck, and chart."
        />
        {onResetPalette && (
          <Button variant="light" color="gray" size="xs" onClick={openReset}>
            Reset palette to defaults
          </Button>
        )}
      </Group>

      <Box>
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} mb="xs" c="gray.7">
          Primary palette
        </Text>
        <SimpleGrid cols={3} spacing="lg" verticalSpacing="lg">
          <Stack gap={2}>
            <ColorInput
              label="Primary"
              format="hex"
              value={`#${palette.primary_color}`}
              onChange={v => handleChange({ ...palette, primary_color: v.replace('#', '') })}
            />
            <Text size="xs" c="gray.6" lh={1.2}>
              Headings, chart primary
            </Text>
          </Stack>
          <Stack gap={2}>
            <ColorInput
              label="Secondary"
              format="hex"
              value={`#${palette.secondary_color}`}
              onChange={v => handleChange({ ...palette, secondary_color: v.replace('#', '') })}
            />
            <Text size="xs" c="gray.6" lh={1.2}>
              Alt rows, secondary fills
            </Text>
          </Stack>
          <Stack gap={2}>
            <ColorInput
              label="Accent"
              format="hex"
              value={`#${palette.accent_color}`}
              onChange={v => handleChange({ ...palette, accent_color: v.replace('#', '') })}
            />
            <Text size="xs" c="gray.6" lh={1.2}>
              Highlights, callouts
            </Text>
          </Stack>
        </SimpleGrid>
      </Box>

      <Box>
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} mb="xs" c="gray.7">
          Extended palette
        </Text>
        <SimpleGrid cols={3} spacing="lg" verticalSpacing="lg">
          {EXTENDED_FIELDS.map(field => {
            const currentValue = palette[field.key];
            const isOverridden = currentValue !== '';
            return (
              <Stack gap={2} key={field.key}>
                <ColorInput
                  label={field.label}
                  format="hex"
                  value={currentValue ? `#${currentValue}` : ''}
                  placeholder={field.derivedHint}
                  onChange={v => handleFieldChange(field.key, v.replace('#', ''))}
                />
                <Group justify="space-between" gap="xs" wrap="nowrap">
                  <Text size="xs" c="gray.6" lh={1.2}>
                    {field.description}
                  </Text>
                  {isOverridden && (
                    <Anchor
                      component="button"
                      type="button"
                      size="xs"
                      onClick={() => handleFieldChange(field.key, '')}
                    >
                      Clear
                    </Anchor>
                  )}
                </Group>
              </Stack>
            );
          })}
        </SimpleGrid>
      </Box>

      <Modal opened={resetOpened} onClose={closeReset} title="Reset colour palette?" centered>
        <Stack gap="md">
          <Text size="sm">
            This will overwrite the saved colour palette for this tenant with the brand defaults
            (primary, secondary, accent, and the extended palette). Typography and brand identity
            are not affected. The change is saved immediately.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={closeReset} disabled={isResetting}>
              Cancel
            </Button>
            <Button color="red" onClick={handleResetConfirmed} loading={isResetting}>
              Reset palette
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
